import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Layers, FlaskConical, BarChart3, ChevronDown, ChevronUp,
  ArrowUp, ArrowDown, Pencil, Save, Rocket, Undo2, Plus, X,
  CheckCircle, AlertCircle, RefreshCw,
} from 'lucide-react';
import {
  adminListMotionModels,
  adminListMotionTiers,
  adminMotionAnalytics,
  adminPublishTierVersion,
  adminReorderTiers,
  adminRollbackTier,
  adminSaveTierDraft,
  adminUpsertMotionModel,
} from '../../services/motion-tiers';
import type {
  AdminMotionModel,
  AdminMotionTier,
  AdminTierActiveVersion,
  AspectRatioBehaviour,
  MotionAnalyticsResponse,
  SaveTierDraftPayload,
  UpsertMotionModelPayload,
} from '../../types/motion-tiers';
import {
  ADAPTER_TYPE_OPTIONS,
  ASPECT_RATIO_BEHAVIOUR_OPTIONS,
} from '../../types/motion-tiers';
import ModelTestingTab from './ModelTestingTab';

type PanelTab = 'tiers' | 'testing' | 'analytics';

interface Banner {
  type: 'success' | 'error';
  message: string;
}

interface TierEditForm {
  display_name: string;
  description: string;
  badge_text: string;
  model_id: string;
  credit_cost: number;
  max_images: number;
  duration_seconds: number;
  resolution: string;
  aspect_ratio_behaviour: AspectRatioBehaviour;
  generate_audio: boolean;
  is_enabled: boolean;
  is_recommended: boolean;
  display_order: number;
  default_prompt: string;
  negative_prompt: string;
  model_settings_json: string;
  estimated_fal_cost_usd: string;
  admin_notes: string;
  fallback_model_id: string;
  fallback_enabled: boolean;
  fallback_max_attempts: number;
}

function money(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toFixed(digits)}`;
}

function pct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

function draftFromTier(tier: AdminMotionTier): TierEditForm {
  const draft = tier.draft_versions?.[0] ?? null;
  const active = tier.active_version;
  const src = draft ?? active;
  const settings = src?.model_settings ?? {};
  return {
    display_name: src?.display_name ?? '',
    description: src?.description ?? '',
    badge_text: src?.badge_text ?? '',
    model_id: src?.model_id ?? '',
    credit_cost: src?.credit_cost ?? 1,
    max_images: src?.max_images ?? 15,
    duration_seconds: src?.duration_seconds ?? 5,
    resolution: src?.resolution ?? '1080p',
    aspect_ratio_behaviour: src?.aspect_ratio_behaviour ?? 'auto',
    generate_audio: Boolean(src?.generate_audio),
    is_enabled: src?.is_enabled !== false,
    is_recommended: Boolean(src?.is_recommended),
    display_order: tier.display_order,
    default_prompt: src?.default_prompt ?? '',
    negative_prompt: src?.negative_prompt ?? '',
    model_settings_json: JSON.stringify(settings ?? {}, null, 2),
    estimated_fal_cost_usd:
      src?.estimated_fal_cost_usd != null ? String(src.estimated_fal_cost_usd) : '',
    admin_notes: src?.admin_notes ?? '',
    fallback_model_id: src?.fallback_model_id ?? '',
    fallback_enabled: Boolean(src?.fallback_enabled),
    fallback_max_attempts: src?.fallback_max_attempts ?? 1,
  };
}

function formToPayload(form: TierEditForm): SaveTierDraftPayload {
  let model_settings: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(form.model_settings_json || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      model_settings = parsed;
    }
  } catch {
    throw new Error('Model settings must be valid JSON');
  }

  return {
    display_name: form.display_name.trim(),
    description: form.description,
    badge_text: form.badge_text.trim() || null,
    model_id: form.model_id || null,
    fallback_model_id: form.fallback_model_id || null,
    fallback_enabled: form.fallback_enabled,
    fallback_max_attempts: form.fallback_max_attempts,
    credit_cost: form.credit_cost,
    max_images: form.max_images,
    duration_seconds: form.duration_seconds,
    resolution: form.resolution || null,
    aspect_ratio_behaviour: form.aspect_ratio_behaviour,
    generate_audio: form.generate_audio,
    is_enabled: form.is_enabled,
    is_recommended: form.is_recommended,
    default_prompt: form.default_prompt || null,
    negative_prompt: form.negative_prompt || null,
    model_settings,
    estimated_fal_cost_usd:
      form.estimated_fal_cost_usd.trim() === ''
        ? null
        : Number(form.estimated_fal_cost_usd),
    admin_notes: form.admin_notes || null,
  };
}

function collectPublishWarnings(
  form: TierEditForm,
  model: AdminMotionModel | undefined,
  active: AdminTierActiveVersion | null
): string[] {
  const warnings: string[] = [];
  if (!model) {
    warnings.push('No model selected');
    return warnings;
  }
  if (!model.is_active) warnings.push('Selected model is inactive');
  if (model.testing_only) warnings.push('Publishing a testing_only model to a customer tier');
  if (form.generate_audio && !model.supports_audio) {
    warnings.push('Model does not support audio but generate_audio is enabled');
  }
  if (
    model.supported_durations.length > 0 &&
    !model.supported_durations.includes(form.duration_seconds)
  ) {
    warnings.push(`Duration ${form.duration_seconds}s may be unsupported by model`);
  }
  if (
    form.resolution &&
    model.supported_resolutions.length > 0 &&
    !model.supported_resolutions.includes(form.resolution)
  ) {
    warnings.push(`Resolution ${form.resolution} may be unsupported by model`);
  }
  if (form.fallback_enabled && !form.fallback_model_id) {
    warnings.push('Fallback enabled but no fallback model selected');
  }
  if (active && active.model_id !== form.model_id) {
    warnings.push(
      `Model change: ${active.model_admin_name ?? 'current'} → ${model.admin_name}`
    );
  }
  return warnings;
}

const emptyModelForm = (): UpsertMotionModelPayload => ({
  endpoint: '',
  admin_name: '',
  provider: 'fal.ai',
  adapter_type: 'kling_v2',
  supported_durations: [5],
  supported_resolutions: ['1080p'],
  supported_aspect_ratios: ['16:9', '9:16', '1:1'],
  supports_audio: false,
  supports_end_frame: false,
  estimated_cost_fixed_usd: null,
  estimated_cost_formula: null,
  is_active: true,
  testing_only: false,
  admin_notes: null,
});

export default function ModelsPricingPanel() {
  const [tab, setTab] = useState<PanelTab>('tiers');
  const [banner, setBanner] = useState<Banner | null>(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<AdminMotionTier[]>([]);
  const [models, setModels] = useState<AdminMotionModel[]>([]);
  const [creditTypical, setCreditTypical] = useState(1.1);
  const [creditConservative, setCreditConservative] = useState(0.67);

  const [editingTier, setEditingTier] = useState<AdminMotionTier | null>(null);
  const [editForm, setEditForm] = useState<TierEditForm | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [rollbackConfirm, setRollbackConfirm] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [modelsOpen, setModelsOpen] = useState(false);
  const [modelForm, setModelForm] = useState<UpsertMotionModelPayload>(emptyModelForm());
  const [savingModel, setSavingModel] = useState(false);

  const [analytics, setAnalytics] = useState<MotionAnalyticsResponse | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFrom, setAnalyticsFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [analyticsTo, setAnalyticsTo] = useState(() => new Date().toISOString().slice(0, 10));

  const showBanner = useCallback((b: Banner) => {
    setBanner(b);
    window.setTimeout(() => setBanner(null), 4500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const [tiersRes, modelsRes] = await Promise.all([
      adminListMotionTiers(),
      adminListMotionModels(),
    ]);
    setLoading(false);

    if (!tiersRes.success) {
      showBanner({ type: 'error', message: tiersRes.error || 'Failed to load tiers' });
    } else {
      setTiers(tiersRes.tiers ?? []);
      if (tiersRes.credit_value_usd_typical != null) {
        setCreditTypical(tiersRes.credit_value_usd_typical);
      }
      if (tiersRes.credit_value_usd_conservative != null) {
        setCreditConservative(tiersRes.credit_value_usd_conservative);
      }
    }

    if (!modelsRes.success) {
      showBanner({ type: 'error', message: modelsRes.error || 'Failed to load models' });
    } else {
      setModels(modelsRes.models ?? []);
    }
  }, [showBanner]);

  useEffect(() => {
    load();
  }, [load]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    const fromIso = new Date(`${analyticsFrom}T00:00:00`).toISOString();
    const toIso = new Date(`${analyticsTo}T23:59:59`).toISOString();
    const res = await adminMotionAnalytics(fromIso, toIso);
    setAnalyticsLoading(false);
    if (!res.success) {
      showBanner({ type: 'error', message: res.error || 'Failed to load analytics' });
      return;
    }
    setAnalytics(res);
  }, [analyticsFrom, analyticsTo, showBanner]);

  useEffect(() => {
    if (tab === 'analytics') loadAnalytics();
  }, [tab, loadAnalytics]);

  const assignableModels = useMemo(() => {
    const currentId = editForm?.model_id;
    return models.filter(
      (m) => (m.is_active && !m.testing_only) || m.id === currentId
    );
  }, [models, editForm?.model_id]);

  const fallbackModels = useMemo(() => {
    const currentId = editForm?.fallback_model_id;
    return models.filter(
      (m) => (m.is_active && !m.testing_only) || m.id === currentId
    );
  }, [models, editForm?.fallback_model_id]);

  const openEdit = (tier: AdminMotionTier) => {
    setEditingTier(tier);
    setEditForm(draftFromTier(tier));
    setPublishConfirm(false);
    setRollbackConfirm(false);
  };

  const closeEdit = () => {
    setEditingTier(null);
    setEditForm(null);
    setPublishConfirm(false);
    setRollbackConfirm(false);
  };

  const handleSaveDraft = async () => {
    if (!editingTier || !editForm) return;
    setSavingDraft(true);
    try {
      const payload = formToPayload(editForm);
      const res = await adminSaveTierDraft(editingTier.id, payload);
      if (!res.success) {
        showBanner({ type: 'error', message: res.error || 'Save failed' });
      } else {
        showBanner({ type: 'success', message: 'Draft saved' });
        await load();
        // refresh edit form from reloaded tier
        const refreshed = (await adminListMotionTiers()).tiers?.find((t) => t.id === editingTier.id);
        if (refreshed) {
          setEditingTier(refreshed);
          setEditForm(draftFromTier(refreshed));
        }
      }
    } catch (e) {
      showBanner({
        type: 'error',
        message: e instanceof Error ? e.message : 'Save failed',
      });
    }
    setSavingDraft(false);
  };

  const handlePublish = async () => {
    if (!editingTier || !editForm) return;
    setPublishing(true);
    try {
      // Ensure draft exists with latest form
      const payload = formToPayload(editForm);
      const saveRes = await adminSaveTierDraft(editingTier.id, payload);
      if (!saveRes.success) {
        showBanner({ type: 'error', message: saveRes.error || 'Could not save draft before publish' });
        setPublishing(false);
        return;
      }
      const versionId =
        (saveRes.version as { id?: string } | undefined)?.id ??
        editingTier.draft_versions?.[0]?.id;
      if (!versionId) {
        // re-fetch to get draft id
        const list = await adminListMotionTiers();
        const t = list.tiers?.find((x) => x.id === editingTier.id);
        const draftId = t?.draft_versions?.[0]?.id;
        if (!draftId) {
          showBanner({ type: 'error', message: 'No draft version to publish' });
          setPublishing(false);
          return;
        }
        const pub = await adminPublishTierVersion(draftId);
        if (!pub.success) {
          showBanner({ type: 'error', message: pub.error || 'Publish failed' });
        } else {
          const warns = Array.isArray(pub.warnings) ? (pub.warnings as string[]) : [];
          showBanner({
            type: 'success',
            message: warns.length
              ? `Published with warnings: ${warns.join('; ')}`
              : 'Tier published',
          });
          closeEdit();
          await load();
        }
        setPublishing(false);
        return;
      }

      const pub = await adminPublishTierVersion(versionId);
      if (!pub.success) {
        showBanner({ type: 'error', message: pub.error || 'Publish failed' });
      } else {
        const warns = Array.isArray(pub.warnings) ? (pub.warnings as string[]) : [];
        showBanner({
          type: 'success',
          message: warns.length
            ? `Published with warnings: ${warns.join('; ')}`
            : 'Tier published',
        });
        closeEdit();
        await load();
      }
    } catch (e) {
      showBanner({
        type: 'error',
        message: e instanceof Error ? e.message : 'Publish failed',
      });
    }
    setPublishing(false);
    setPublishConfirm(false);
  };

  const handleRollback = async () => {
    if (!editingTier) return;
    setRollingBack(true);
    const res = await adminRollbackTier(editingTier.id);
    setRollingBack(false);
    setRollbackConfirm(false);
    if (!res.success) {
      showBanner({ type: 'error', message: res.error || 'Rollback failed' });
      return;
    }
    showBanner({ type: 'success', message: 'Rolled back to previous version' });
    closeEdit();
    await load();
  };

  const moveTier = async (tierId: string, direction: -1 | 1) => {
    const sorted = [...tiers].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((t) => t.id === tierId);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setReordering(true);
    const res = await adminReorderTiers(next.map((t) => t.id));
    setReordering(false);
    if (!res.success) {
      showBanner({ type: 'error', message: res.error || 'Reorder failed' });
      return;
    }
    await load();
  };

  const handleSaveModel = async () => {
    if (!modelForm.endpoint.trim() || !modelForm.admin_name.trim()) {
      showBanner({ type: 'error', message: 'Endpoint and admin name are required' });
      return;
    }
    setSavingModel(true);
    const res = await adminUpsertMotionModel(modelForm);
    setSavingModel(false);
    if (!res.success) {
      showBanner({ type: 'error', message: res.error || 'Failed to save model' });
      return;
    }
    showBanner({
      type: 'success',
      message: res.created ? 'Model created' : 'Model updated',
    });
    setModelForm(emptyModelForm());
    await load();
  };

  const selectedModel = models.find((m) => m.id === editForm?.model_id);
  const publishWarnings =
    editingTier && editForm
      ? collectPublishWarnings(editForm, selectedModel, editingTier.active_version)
      : [];

  const costDelta =
    editingTier?.active_version && editForm
      ? (Number(editForm.estimated_fal_cost_usd) || 0) -
        (Number(editingTier.active_version.estimated_fal_cost_usd) || 0)
      : null;

  return (
    <div className="relative">
      {banner && (
        <div
          className={`mx-6 mt-4 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 ${
            banner.type === 'success'
              ? 'bg-green-900/40 text-green-300 border border-green-800/50'
              : 'bg-red-900/40 text-red-300 border border-red-800/50'
          }`}
        >
          {banner.type === 'success' ? (
            <CheckCircle className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          <span className="flex-1">{banner.message}</span>
          <button type="button" onClick={() => setBanner(null)} className="opacity-70 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-1 px-6 pt-4 border-b border-slate-800">
        {(
          [
            { id: 'tiers' as const, label: 'Quality Tiers', icon: Layers },
            { id: 'testing' as const, label: 'Model Testing', icon: FlaskConical },
            { id: 'analytics' as const, label: 'Analytics', icon: BarChart3 },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          className="ml-auto mb-1 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="px-6 py-5">
        {loading && tab !== 'testing' ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : tab === 'tiers' ? (
          <div className="space-y-6">
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3 text-xs text-slate-400 leading-relaxed space-y-1.5">
              <p>
                <span className="text-slate-200 font-medium">How this works:</span> customers
                pick a quality tier (name, description, credits). You assign which fal.ai model
                powers each tier. Changing the model here updates <em>new</em> generations only —
                jobs already running keep their snapshot.
              </p>
              <p>
                Edit → <span className="text-slate-300">Save as draft</span> to stage a change,
                then <span className="text-slate-300">Publish</span> when ready. Use Model Testing
                to compare engines before switching live. Kling durations are 5 or 10s only;
                Seedance/Hailuo can use 6s.
              </p>
            </div>
            <div className="space-y-3">
              {tiers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-12">No tiers found</p>
              ) : (
                [...tiers]
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((tier, index, arr) => {
                    const v = tier.active_version;
                    const hasDraft = (tier.draft_versions?.length ?? 0) > 0;
                    return (
                      <div
                        key={tier.id}
                        className="border border-slate-800 rounded-xl bg-slate-900/40 overflow-hidden"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-4">
                          <div className="flex items-center gap-1 sm:flex-col sm:pt-1">
                            <button
                              type="button"
                              disabled={reordering || index === 0}
                              onClick={() => moveTier(tier.id, -1)}
                              className="p-1.5 text-slate-500 hover:text-white disabled:opacity-30 rounded"
                              title="Move up"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              disabled={reordering || index === arr.length - 1}
                              onClick={() => moveTier(tier.id, 1)}
                              className="p-1.5 text-slate-500 hover:text-white disabled:opacity-30 rounded"
                              title="Move down"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-white font-semibold">
                                {v?.display_name ?? tier.tier_key}
                              </h3>
                              <span className="text-[10px] font-mono text-slate-500">
                                {tier.tier_key}
                              </span>
                              {v?.is_recommended && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-400">
                                  Recommended
                                </span>
                              )}
                              {v?.is_enabled === false && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">
                                  Disabled
                                </span>
                              )}
                              {hasDraft && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-400">
                                  Draft pending
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-400 mb-2">
                              <div>
                                Model:{' '}
                                <span className="text-slate-200">
                                  {v?.model_admin_name ?? '—'}
                                </span>
                              </div>
                              <div className="col-span-2 md:col-span-3 font-mono text-[11px] truncate" title={v?.model_endpoint ?? undefined}>
                                {v?.model_endpoint ?? '—'}
                              </div>
                              <div>
                                Credits:{' '}
                                <span className="text-amber-400 font-medium">
                                  {v?.credit_cost ?? '—'}
                                </span>
                              </div>
                              <div>
                                Max images:{' '}
                                <span className="text-slate-200 font-medium">
                                  {v?.max_images ?? '—'}
                                </span>
                              </div>
                              <div>Fal est.: {money(v?.estimated_fal_cost_usd)}</div>
                              <div>
                                {v?.duration_seconds ?? '—'}s · {v?.resolution ?? '—'}
                              </div>
                              <div>
                                Updated{' '}
                                {tier.updated_at
                                  ? new Date(tier.updated_at).toLocaleDateString()
                                  : '—'}
                              </div>
                            </div>

                            {v && (
                              <p className="text-[11px] text-slate-500 leading-relaxed">
                                Cost {money(v.estimated_fal_cost_usd)} · Revenue typical{' '}
                                {money(v.revenue_typical_usd)} / conservative{' '}
                                {money(v.revenue_conservative_usd)} · Gross profit{' '}
                                {money(v.profit_typical_usd)} ({pct(v.margin_typical)}) · Per 100{' '}
                                {money(v.cost_per_100_usd)} · Per 1000 {money(v.cost_per_1000_usd)}
                                <span className="text-slate-600">
                                  {' '}
                                  (credit ${creditTypical.toFixed(2)} / ${creditConservative.toFixed(2)})
                                </span>
                              </p>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => openEdit(tier)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-lg shrink-0"
                          >
                            <Pencil className="w-3 h-3" />
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Manage models */}
            <div className="border border-slate-800 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setModelsOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-900/60 text-sm text-slate-200 hover:bg-slate-900"
              >
                <span className="font-medium">Manage models ({models.length})</span>
                {modelsOpen ? (
                  <ChevronUp className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                )}
              </button>
              {modelsOpen && (
                <div className="p-4 space-y-4 border-t border-slate-800">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-slate-500 border-b border-slate-800">
                        <tr>
                          <th className="py-2 pr-3 font-medium">Name</th>
                          <th className="py-2 pr-3 font-medium">Endpoint</th>
                          <th className="py-2 pr-3 font-medium">Adapter</th>
                          <th className="py-2 pr-3 font-medium">Cost</th>
                          <th className="py-2 pr-3 font-medium">Flags</th>
                          <th className="py-2 font-medium" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60">
                        {models.map((m) => (
                          <tr key={m.id} className="text-slate-300">
                            <td className="py-2 pr-3 font-medium text-white">{m.admin_name}</td>
                            <td className="py-2 pr-3 font-mono text-[10px] text-slate-500 max-w-[220px] truncate" title={m.endpoint}>
                              {m.endpoint}
                            </td>
                            <td className="py-2 pr-3">{m.adapter_type}</td>
                            <td className="py-2 pr-3">{money(m.estimated_cost_fixed_usd)}</td>
                            <td className="py-2 pr-3">
                              {!m.is_active && (
                                <span className="text-red-400 mr-1">inactive</span>
                              )}
                              {m.testing_only && (
                                <span className="text-amber-400">testing</span>
                              )}
                              {m.is_active && !m.testing_only && (
                                <span className="text-green-400">live</span>
                              )}
                            </td>
                            <td className="py-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setModelForm({
                                    id: m.id,
                                    endpoint: m.endpoint,
                                    admin_name: m.admin_name,
                                    provider: m.provider,
                                    adapter_type: m.adapter_type,
                                    supported_durations: m.supported_durations,
                                    supported_resolutions: m.supported_resolutions,
                                    supported_aspect_ratios: m.supported_aspect_ratios,
                                    supports_audio: m.supports_audio,
                                    supports_end_frame: m.supports_end_frame,
                                    estimated_cost_fixed_usd: m.estimated_cost_fixed_usd,
                                    estimated_cost_formula: m.estimated_cost_formula,
                                    is_active: m.is_active,
                                    testing_only: m.testing_only,
                                    admin_notes: m.admin_notes,
                                  })
                                }
                                className="text-blue-400 hover:text-blue-300"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/50 space-y-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Plus className="w-4 h-4 text-blue-400" />
                      {modelForm.id ? 'Edit model' : 'Add model'}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Admin name">
                        <input
                          value={modelForm.admin_name}
                          onChange={(e) =>
                            setModelForm((f) => ({ ...f, admin_name: e.target.value }))
                          }
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Endpoint (fal)">
                        <input
                          value={modelForm.endpoint}
                          onChange={(e) =>
                            setModelForm((f) => ({ ...f, endpoint: e.target.value }))
                          }
                          className={inputCls}
                          placeholder="fal-ai/..."
                        />
                      </Field>
                      <Field label="Adapter">
                        <select
                          value={modelForm.adapter_type}
                          onChange={(e) =>
                            setModelForm((f) => ({
                              ...f,
                              adapter_type: e.target.value as UpsertMotionModelPayload['adapter_type'],
                            }))
                          }
                          className={inputCls}
                        >
                          {ADAPTER_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Est. cost USD">
                        <input
                          type="number"
                          step="0.01"
                          value={modelForm.estimated_cost_fixed_usd ?? ''}
                          onChange={(e) =>
                            setModelForm((f) => ({
                              ...f,
                              estimated_cost_fixed_usd:
                                e.target.value === '' ? null : Number(e.target.value),
                            }))
                          }
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Durations (comma)">
                        <input
                          value={(modelForm.supported_durations ?? []).join(',')}
                          onChange={(e) =>
                            setModelForm((f) => ({
                              ...f,
                              supported_durations: e.target.value
                                .split(',')
                                .map((s) => Number(s.trim()))
                                .filter((n) => Number.isFinite(n)),
                            }))
                          }
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Resolutions (comma)">
                        <input
                          value={(modelForm.supported_resolutions ?? []).join(',')}
                          onChange={(e) =>
                            setModelForm((f) => ({
                              ...f,
                              supported_resolutions: e.target.value
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean),
                            }))
                          }
                          className={inputCls}
                        />
                      </Field>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-300">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={modelForm.is_active !== false}
                          onChange={(e) =>
                            setModelForm((f) => ({ ...f, is_active: e.target.checked }))
                          }
                        />
                        Active
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(modelForm.testing_only)}
                          onChange={(e) =>
                            setModelForm((f) => ({ ...f, testing_only: e.target.checked }))
                          }
                        />
                        Testing only
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(modelForm.supports_audio)}
                          onChange={(e) =>
                            setModelForm((f) => ({ ...f, supports_audio: e.target.checked }))
                          }
                        />
                        Audio
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(modelForm.supports_end_frame)}
                          onChange={(e) =>
                            setModelForm((f) => ({ ...f, supports_end_frame: e.target.checked }))
                          }
                        />
                        End frame
                      </label>
                    </div>
                    <Field label="Admin notes">
                      <textarea
                        value={modelForm.admin_notes ?? ''}
                        onChange={(e) =>
                          setModelForm((f) => ({ ...f, admin_notes: e.target.value }))
                        }
                        rows={2}
                        className={inputCls}
                      />
                    </Field>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveModel}
                        disabled={savingModel}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg"
                      >
                        {savingModel ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Save className="w-3 h-3" />
                        )}
                        {modelForm.id ? 'Update model' : 'Add model'}
                      </button>
                      {modelForm.id && (
                        <button
                          type="button"
                          onClick={() => setModelForm(emptyModelForm())}
                          className="px-3 py-1.5 text-xs text-slate-400 hover:text-white"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : tab === 'testing' ? (
          <ModelTestingTab models={models} onBanner={showBanner} />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="From">
                <input
                  type="date"
                  value={analyticsFrom}
                  onChange={(e) => setAnalyticsFrom(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="To">
                <input
                  type="date"
                  value={analyticsTo}
                  onChange={(e) => setAnalyticsTo(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <button
                type="button"
                onClick={loadAnalytics}
                disabled={analyticsLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-sm text-white rounded-lg disabled:opacity-50"
              >
                {analyticsLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <BarChart3 className="w-3.5 h-3.5" />
                )}
                Refresh
              </button>
            </div>

            {analyticsLoading && !analytics ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : analytics?.summary ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Generations', value: analytics.summary.total_generations },
                    { label: 'Success rate', value: pct(analytics.summary.success_rate) },
                    { label: 'Spend', value: money(analytics.summary.estimated_spend_usd) },
                    { label: 'Revenue', value: money(analytics.summary.estimated_revenue_usd) },
                    { label: 'Margin', value: pct(analytics.summary.estimated_margin) },
                    { label: 'Failed', value: analytics.summary.failed },
                    { label: 'Fallbacks', value: analytics.summary.fallback_attempts },
                    {
                      label: 'Avg time',
                      value:
                        analytics.summary.avg_processing_time_ms != null
                          ? `${(analytics.summary.avg_processing_time_ms / 1000).toFixed(1)}s`
                          : '—',
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center"
                    >
                      <p className="text-lg font-bold text-slate-200">{s.value}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <MiniTable
                    title="By tier"
                    headers={['Tier', 'Gens', 'Spend', 'Credits']}
                    rows={(analytics.by_tier ?? []).map((r) => [
                      r.tier_name || r.tier_key || '—',
                      String(r.generations),
                      money(r.estimated_spend_usd),
                      String(r.credits_charged),
                    ])}
                  />
                  <MiniTable
                    title="By model"
                    headers={['Model', 'Gens', 'Fail', 'Spend']}
                    rows={(analytics.by_model ?? []).map((r) => [
                      r.model_admin_name || r.fal_endpoint || '—',
                      String(r.generations),
                      String(r.failed),
                      money(r.estimated_spend_usd),
                    ])}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 text-center py-12">No analytics data</p>
            )}
          </div>
        )}
      </div>

      {/* Edit drawer */}
      {editingTier && editForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex justify-end">
          <div className="w-full max-w-lg h-full bg-slate-950 border-l border-slate-800 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-slate-950">
              <div>
                <h3 className="text-white font-semibold">Edit tier</h3>
                <p className="text-xs text-slate-500 font-mono">{editingTier.tier_key}</p>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <Field label="Internal tier key (read-only)">
                <input value={editingTier.tier_key} disabled className={`${inputCls} opacity-60`} />
              </Field>
              <Field label="Display name">
                <input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={2}
                  className={inputCls}
                />
              </Field>
              <Field label="Badge text">
                <input
                  value={editForm.badge_text}
                  onChange={(e) => setEditForm({ ...editForm, badge_text: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Model">
                <select
                  value={editForm.model_id}
                  onChange={(e) => setEditForm({ ...editForm, model_id: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select model…</option>
                  {assignableModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.admin_name}
                      {m.testing_only ? ' (testing)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
              {selectedModel && (
                <p className="text-[11px] font-mono text-slate-500 truncate" title={selectedModel.endpoint}>
                  {selectedModel.endpoint}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Credit cost (flat / video)">
                  <input
                    type="number"
                    min={1}
                    value={editForm.credit_cost}
                    onChange={(e) =>
                      setEditForm({ ...editForm, credit_cost: Number(e.target.value) || 1 })
                    }
                    className={inputCls}
                  />
                </Field>
                <Field label="Max images">
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={editForm.max_images}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        max_images: Math.min(50, Math.max(1, Number(e.target.value) || 1)),
                      })
                    }
                    className={inputCls}
                  />
                </Field>
                <Field label="Duration (s)">
                  <input
                    type="number"
                    min={1}
                    value={editForm.duration_seconds}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        duration_seconds: Number(e.target.value) || 5,
                      })
                    }
                    className={inputCls}
                  />
                </Field>
                <Field label="Resolution">
                  <input
                    value={editForm.resolution}
                    onChange={(e) => setEditForm({ ...editForm, resolution: e.target.value })}
                    className={inputCls}
                  />
                </Field>
                <Field label="Aspect behaviour">
                  <select
                    value={editForm.aspect_ratio_behaviour}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        aspect_ratio_behaviour: e.target.value,
                      })
                    }
                    className={inputCls}
                  >
                    {ASPECT_RATIO_BEHAVIOUR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Est. fal cost USD">
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.estimated_fal_cost_usd}
                    onChange={(e) =>
                      setEditForm({ ...editForm, estimated_fal_cost_usd: e.target.value })
                    }
                    className={inputCls}
                  />
                </Field>
                <Field label="Display order">
                  <input
                    type="number"
                    value={editForm.display_order}
                    disabled
                    className={`${inputCls} opacity-60`}
                    title="Use up/down arrows on the list to reorder"
                  />
                </Field>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-slate-300 py-1">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.generate_audio}
                    onChange={(e) =>
                      setEditForm({ ...editForm, generate_audio: e.target.checked })
                    }
                  />
                  Generate audio
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.is_enabled}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_enabled: e.target.checked })
                    }
                  />
                  Enabled
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.is_recommended}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_recommended: e.target.checked })
                    }
                  />
                  Recommended
                </label>
              </div>
              <Field label="Default prompt">
                <textarea
                  value={editForm.default_prompt}
                  onChange={(e) =>
                    setEditForm({ ...editForm, default_prompt: e.target.value })
                  }
                  rows={3}
                  className={inputCls}
                />
              </Field>
              <Field label="Negative prompt">
                <textarea
                  value={editForm.negative_prompt}
                  onChange={(e) =>
                    setEditForm({ ...editForm, negative_prompt: e.target.value })
                  }
                  rows={2}
                  className={inputCls}
                />
              </Field>
              <Field label="Model-specific JSON settings">
                <textarea
                  value={editForm.model_settings_json}
                  onChange={(e) =>
                    setEditForm({ ...editForm, model_settings_json: e.target.value })
                  }
                  rows={4}
                  className={`${inputCls} font-mono text-[11px]`}
                />
              </Field>
              <Field label="Admin notes">
                <textarea
                  value={editForm.admin_notes}
                  onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                  rows={2}
                  className={inputCls}
                />
              </Field>
              <div className="border border-slate-800 rounded-lg p-3 space-y-3">
                <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    checked={editForm.fallback_enabled}
                    onChange={(e) =>
                      setEditForm({ ...editForm, fallback_enabled: e.target.checked })
                    }
                  />
                  Fallback enabled
                </label>
                <Field label="Fallback model">
                  <select
                    value={editForm.fallback_model_id}
                    onChange={(e) =>
                      setEditForm({ ...editForm, fallback_model_id: e.target.value })
                    }
                    className={inputCls}
                  >
                    <option value="">None</option>
                    {fallbackModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.admin_name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Max attempts">
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={editForm.fallback_max_attempts}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        fallback_max_attempts: Number(e.target.value) || 1,
                      })
                    }
                    className={inputCls}
                  />
                </Field>
              </div>
            </div>

            <div className="sticky bottom-0 border-t border-slate-800 bg-slate-950 px-5 py-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white text-sm rounded-lg"
              >
                {savingDraft ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Save as Draft
              </button>
              <button
                type="button"
                onClick={() => setPublishConfirm(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg"
              >
                <Rocket className="w-3.5 h-3.5" />
                Publish
              </button>
              <button
                type="button"
                onClick={() => setRollbackConfirm(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-900/40 hover:bg-amber-900/60 text-amber-300 text-sm rounded-lg"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Rollback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish confirm */}
      {publishConfirm && editingTier && editForm && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-slate-950 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-3">Publish tier version?</h3>
            <div className="text-sm text-slate-400 space-y-2 mb-4">
              <p>
                Model:{' '}
                <span className="text-slate-200">
                  {editingTier.active_version?.model_admin_name ?? '—'}
                </span>{' '}
                → <span className="text-blue-400">{selectedModel?.admin_name ?? '—'}</span>
              </p>
              <p>
                Est. fal cost:{' '}
                {money(editingTier.active_version?.estimated_fal_cost_usd)} →{' '}
                {money(
                  editForm.estimated_fal_cost_usd
                    ? Number(editForm.estimated_fal_cost_usd)
                    : null
                )}
                {costDelta != null && Number.isFinite(costDelta) && (
                  <span className={costDelta > 0 ? ' text-red-400' : ' text-green-400'}>
                    {' '}
                    ({costDelta >= 0 ? '+' : ''}
                    {costDelta.toFixed(2)})
                  </span>
                )}
              </p>
              <p>
                Credits: {editingTier.active_version?.credit_cost ?? '—'} → {editForm.credit_cost}
              </p>
              {publishWarnings.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {publishWarnings.map((w) => (
                    <li key={w} className="text-amber-400 text-xs flex gap-1.5">
                      <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                      {w}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPublishConfirm(false)}
                className="px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={publishing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                Confirm publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rollback confirm */}
      {rollbackConfirm && editingTier && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-slate-950 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-2">Rollback tier?</h3>
            <p className="text-sm text-slate-400 mb-4">
              This reactivates the previous archived version of{' '}
              <span className="text-slate-200">{editingTier.tier_key}</span> and archives the
              current active version.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRollbackConfirm(false)}
                className="px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRollback}
                disabled={rollingBack}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
              >
                {rollingBack ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Undo2 className="w-4 h-4" />
                )}
                Confirm rollback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  'w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function MiniTable({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="border border-slate-800 rounded-xl overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-800 text-xs font-medium text-slate-300">
        {title}
      </div>
      <div className="overflow-x-auto max-h-64 overflow-y-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500 sticky top-0 bg-slate-950">
            <tr>
              {headers.map((h) => (
                <th key={h} className="px-3 py-2 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60 text-slate-300">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={headers.length} className="px-3 py-6 text-slate-500 text-center">
                  No data
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 truncate max-w-[160px]" title={cell}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
